
import { useLocation, useNavigate } from 'react-router-dom';
import { AlertCircle, ArrowLeft, Wifi, Database, RefreshCw, ShieldAlert, Clock } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { useEffect, useState } from 'react';
import { supabase } from "@/integrations/supabase/client";
import { debugLogger } from '@/utils/debugLogger';

interface ErrorInfo {
  title: string;
  description: string;
  action?: string;
  redirectTo?: string;
  icon: React.ElementType;
  showRetryButton?: boolean;
}

const DEFAULT_ERROR: ErrorInfo = {
  title: "Algo deu errado",
  description: "Ocorreu um erro ao carregar esta página. Por favor, tente novamente mais tarde.",
  action: "Voltar para o login",
  redirectTo: "/main/login",
  icon: AlertCircle
};

const REDIRECT_LOOP_ERROR: ErrorInfo = {
  title: "Detectamos um loop de redirecionamento",
  description: "O sistema detectou muitos redirecionamentos em sequência. Isso pode indicar um problema com sua conta ou permissões.",
  action: "Voltar para o login",
  redirectTo: "/main/login",
  icon: Clock
};

const CONNECTION_ERROR: ErrorInfo = {
  title: "Erro de conexão",
  description: "Não foi possível conectar ao servidor. Verifique sua conexão com a internet e tente novamente.",
  action: "Voltar para o login",
  redirectTo: "/main/login",
  icon: Wifi,
  showRetryButton: true
};

const DATABASE_ERROR: ErrorInfo = {
  title: "Erro ao acessar o banco de dados",
  description: "Não foi possível acessar os dados necessários. Isto pode ser um problema temporário.",
  action: "Tentar novamente",
  redirectTo: "/main/login",
  icon: Database,
  showRetryButton: true
};

const PERMISSION_ERROR: ErrorInfo = {
  title: "Acesso negado",
  description: "Você não tem permissão para acessar esta página. Verifique se você está logado com a conta correta.",
  action: "Voltar para o login",
  redirectTo: "/main/login",
  icon: ShieldAlert,
};

const ERROR_TYPES: Record<string, ErrorInfo> = {
  "redirect-loop": REDIRECT_LOOP_ERROR,
  "connection": CONNECTION_ERROR,
  "database": DATABASE_ERROR,
  "permission": PERMISSION_ERROR,
  "default": DEFAULT_ERROR
};

const ErrorPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isRetrying, setIsRetrying] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [sessionChecked, setSessionChecked] = useState(false);
  const [hasSession, setHasSession] = useState(false);
  
  const errorType = location.state?.errorType || "default";
  const errorInfo = ERROR_TYPES[errorType] || DEFAULT_ERROR;
  const IconComponent = errorInfo.icon;
  
  // Check session status on mount
  useEffect(() => {
    async function checkSession() {
      try {
        const { data } = await supabase.auth.getSession();
        setHasSession(!!data.session);
      } catch (error) {
        debugLogger.error('Error checking session:', error);
      } finally {
        setSessionChecked(true);
      }
    }
    
    checkSession();
  }, []);
  
  const handleAction = () => {
    if (errorInfo.redirectTo) {
      // If we have a session, redirect to feed instead of login
      if (hasSession && errorInfo.redirectTo === '/main/login') {
        navigate("/main/feed", { replace: true });
      } else {
        navigate(errorInfo.redirectTo, { replace: true });
      }
    } else {
      navigate("/main/login", { replace: true });
    }
  };

  const handleRetry = () => {
    setIsRetrying(true);
    setRetryCount(prev => prev + 1);
    
    // Get the previous path or default to feed
    const previousPath = location.state?.from || (hasSession ? "/main/feed" : "/main/login");
    
    // Add a delay to give the impression of trying to reconnect
    setTimeout(() => {
      navigate(previousPath, { replace: true });
      setIsRetrying(false);
    }, 1000);
  };

  // If we've retried too many times, show the redirect loop error
  useEffect(() => {
    if (retryCount >= 3 && errorType !== 'redirect-loop') {
      navigate('/main/error', { 
        state: { 
          errorType: 'redirect-loop',
          originalError: errorType
        },
        replace: true 
      });
    }
  }, [retryCount, navigate, errorType]);

  return (
    <div className="min-h-screen bg-black flex flex-col items-center justify-center p-6">
      <div className="w-full max-w-md bg-gray-900/50 p-8 rounded-lg border border-white/10 shadow-lg text-center">
        <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
          <IconComponent className="h-8 w-8 text-red-500" />
        </div>
        
        <h1 className="text-2xl font-serif text-white mb-3">{errorInfo.title}</h1>
        <p className="text-white/70 mb-8">{errorInfo.description}</p>
        
        <div className="flex flex-col gap-3">
          {errorInfo.action && (
            <Button 
              onClick={handleAction}
              className="bg-luxury-red hover:bg-luxury-red/90 text-white w-full"
              disabled={isRetrying || !sessionChecked}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              {errorInfo.action}
            </Button>
          )}
          
          {errorInfo.showRetryButton && (
            <Button 
              onClick={handleRetry}
              variant="outline" 
              className="border-luxury-red/50 text-luxury-red hover:bg-luxury-red/10"
              disabled={isRetrying || !sessionChecked}
            >
              {isRetrying ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  Tentando reconectar...
                </>
              ) : (
                <>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Tentar novamente
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ErrorPage;
